import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { lteCellAvailabilityCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the LteCellAvailabilityCell component
 */
interface LteCellAvailabilityCellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
}

/**
 * LTE Cell Availability Chart (Cell Level)
 * 
 * Displays availability metrics for individual LTE cells, showing the percentage of time
 * the cell is available and operational for serving users.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level availability metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <LteCellAvailabilityCell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (lteCellAvailabilityCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/lte-cell-availability?cell_name={cellName}
 */

const LteCellAvailabilityCell: React.FC<LteCellAvailabilityCellProps> = ({ 
  cellName,
  onFullscreenToggle, 
  isFullscreen 
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={lteCellAvailabilityCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default LteCellAvailabilityCell;
